import jsPDF from 'jspdf';
import { CSVRow, ServiceStatusDescription, ServiceTask } from '../hooks/useCSVData';

interface PdfReportOptions {
  title: string;
  filenamePrefix: string;
  rows: CSVRow[];
  latestStatuses: Map<string, ServiceStatusDescription>;
  pendingTasksByInitiative: Map<string, ServiceTask[]>;
}

function getFieldValue(row: CSVRow, keys: string[]): string {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function getServiceName(row: CSVRow): string {
  return getFieldValue(row, ['NombreProceso', 'Nombre del servicio']);
}

function getServiceEstado(row: CSVRow): string {
  return getFieldValue(row, ['Estado_implementacion', 'Estado implementación']) || 'Sin estado';
}

function normalizeText(value?: string | null): string {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  return text || '-';
}

function parseUsers(raw?: string): string {
  if (!raw || !raw.trim()) return '-';
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      const names = parsed
        .map((item) => String(item?.nombre || item?.user_mail || item?.email || '').trim())
        .filter(Boolean);
      return names.length ? names.join(', ') : '-';
    }
  } catch {
    // fallback below
  }
  return raw.replace(/[;]/g, ', ');
}

function taskStatusLabel(status: ServiceTask['estado']): string {
  if (status === 'in_progress') return 'En curso';
  if (status === 'completed') return 'Completada';
  return 'Pendiente';
}

function statusColor(estado: string): [number, number, number] {
  const normalized = estado.toLowerCase();
  if (normalized.includes('implementado')) return [16, 150, 72];
  if (normalized.includes('desarrollo')) return [194, 120, 3];
  if (normalized.includes('prueba')) return [219, 105, 14];
  if (normalized.includes('cancel')) return [185, 28, 28];
  return [100, 116, 139];
}

function drawTextBlock(doc: jsPDF, text: string, x: number, y: number, maxWidth: number, lineHeight: number): number {
  const lines = doc.splitTextToSize(text, maxWidth) as string[];
  lines.forEach((line) => {
    doc.text(line, x, y);
    y += lineHeight;
  });
  return y;
}

function ensureSpace(doc: jsPDF, y: number, needed: number, pageWidth: number, pageHeight: number): number {
  if (y + needed <= pageHeight - 44) return y;
  doc.addPage();
  drawFooter(doc, pageWidth, pageHeight);
  return 42;
}

function drawFooter(doc: jsPDF, pageWidth: number, pageHeight: number) {
  doc.setDrawColor(203, 213, 225);
  doc.line(28, pageHeight - 30, pageWidth - 28, pageHeight - 30);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  doc.text('Estado Iniciativas GPT-CEU', 28, pageHeight - 18);
  const pageText = `Página ${doc.getCurrentPageInfo().pageNumber}`;
  doc.text(pageText, pageWidth - 28 - doc.getTextWidth(pageText), pageHeight - 18);
}

export function exportServiceInitiativesPdfReport(options: PdfReportOptions) {
  const ordered = [...options.rows].sort((a, b) => {
    const favDiff = Number(b.__is_favorite === 'true') - Number(a.__is_favorite === 'true');
    if (favDiff !== 0) return favDiff;
    return getServiceName(a).localeCompare(getServiceName(b), 'es', { sensitivity: 'base' });
  });

  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  doc.setFillColor(15, 52, 96);
  doc.rect(0, 0, pageWidth, 88, 'F');
  doc.setFillColor(26, 95, 180);
  doc.rect(0, 88, pageWidth, 4, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text(options.title, 28, 38);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(200, 215, 235);
  doc.text(`${ordered.length} iniciativas · generado el ${new Date().toLocaleString('es-ES')}`, 28, 58);
  drawFooter(doc, pageWidth, pageHeight);

  let y = 122;
  ordered.forEach((row, index) => {
    const initiativeId = row.__database_id;
    const latest = initiativeId ? options.latestStatuses.get(initiativeId) : undefined;
    const tasks = initiativeId ? options.pendingTasksByInitiative.get(initiativeId) || [] : [];
    const name = getServiceName(row) || 'Servicio sin nombre';
    const estado = getServiceEstado(row);
    const blockHeight = 72 + Math.min(120, tasks.length * 18) + (latest ? 34 : 0);

    y = ensureSpace(doc, y, blockHeight, pageWidth, pageHeight);
    const isAlt = index % 2 === 1;
    doc.setFillColor(isAlt ? 245 : 255, isAlt ? 248 : 255, isAlt ? 252 : 255);
    doc.roundedRect(24, y - 18, pageWidth - 48, Math.max(64, blockHeight - 8), 6, 6, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(15, 52, 96);
    doc.text(`${index + 1}. ${name}`, 36, y);

    doc.setFontSize(8);
    doc.setTextColor(...statusColor(estado));
    doc.text(estado, 36, y + 16);
    if (row.Centro) {
      doc.setTextColor(100, 116, 139);
      doc.text(row.Centro, 36, y + 29);
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(35, 35, 35);
    let descY = drawTextBlock(doc, normalizeText(row.descripcion || row['Descripción']), 180, y, 282, 10);
    if (latest?.description) {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 52, 96);
      doc.text('Estado actual:', 180, descY + 8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(35, 35, 35);
      descY = drawTextBlock(doc, latest.description, 180, descY + 20, 282, 10);
    }

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 52, 96);
    doc.text('Responsables', 486, y);
    doc.text('Equipo técnico', 610, y);
    doc.text('Tareas pendientes', 728, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(35, 35, 35);
    drawTextBlock(doc, parseUsers(row['Responsable servicio']), 486, y + 14, 104, 10);
    drawTextBlock(doc, parseUsers(row['Técnico']), 610, y + 14, 96, 10);
    if (tasks.length) {
      let taskY = y + 14;
      tasks.slice(0, 6).forEach((task) => {
        const assignee = task.asignado_a ? ` -> ${task.asignado_a}` : ' -> Sin asignar';
        taskY = drawTextBlock(doc, `- ${task.titulo} [${taskStatusLabel(task.estado)}]${assignee}`, 728, taskY, 86, 10);
      });
      if (tasks.length > 6) {
        doc.text(`+${tasks.length - 6} más`, 728, taskY);
      }
    } else {
      doc.setTextColor(100, 116, 139);
      doc.text('-', 728, y + 14);
    }

    y += Math.max(82, blockHeight);
  });

  const date = new Date().toISOString().split('T')[0];
  doc.save(`${options.filenamePrefix}_${date}.pdf`);
}
